'use client';

import {useEffect, useRef} from 'react';

import CSSUtility from '@/styles/utility.module.scss';
import {useWindowSize, type WindowSizes} from './window-size';

/**
 * Temporarily hides the scrollbar.
 *
 * @param windowSizeThreshold - The window size threshold category.
 * If provided, the scrollbar is hidden only if the current window
 * size matches this category.
 */
export function useHideScrollbar(
  isHidden: boolean,
  windowSizeThreshold?: WindowSizes
) {
  const windowSize = useWindowSize();
  const scrollY = useRef(typeof window === 'undefined' ? 0 : window.scrollY);

  const hasWindowSize = windowSizeThreshold !== undefined;
  const isSizeThreshold = hasWindowSize
    ? windowSize[windowSizeThreshold]
    : false;

  // Hiding the scrollbar while preserving the scroll position
  useEffect(() => {
    if (hasWindowSize && !isSizeThreshold) return;

    if (isHidden) {
      scrollY.current = window.scrollY;
      document.documentElement.style.position = 'fixed';
      document.documentElement.style.top = `-${scrollY.current}px`;
    }

    return () => {
      if (isHidden) {
        document.documentElement.style.position = '';
        document.documentElement.style.top = '';
        document.documentElement.scrollTo(0, scrollY.current);
      }
    };
  }, [isHidden, hasWindowSize, isSizeThreshold]);
}

/**
 * Temporarily hides the scrollbar while preserving the scroll functionality.
 *
 * @param startDelay - A delay in milliseconds before performing the action.
 * @param endDelay - A delay in milliseconds before reverting the action.
 * @param className - An additional CSS class to apply to the `<body>`
 * element when hiding the scrollbar.
 * @param windowSizeThreshold - The window size threshold category.
 * If provided, the scrollbar is hidden only if the current window
 * size matches this category.
 */
export function useTransparentScrollbar(
  isHidden: boolean,
  startDelay?: number,
  endDelay?: number,
  className?: HTMLElement['className'],
  windowSizeThreshold?: WindowSizes
) {
  const windowSize = useWindowSize();

  const hasWindowSize = windowSizeThreshold !== undefined;
  const isSizeThreshold = hasWindowSize
    ? windowSize[windowSizeThreshold]
    : false;

  useEffect(() => {
    if (hasWindowSize && !isSizeThreshold) {
      return;
    }

    const elements = [document.body, document.documentElement];
    const classes = [CSSUtility.scrollbarTransparent];
    if (className) {
      classes.push(...className.split(' '));
    }

    const callback = () => {
      for (const element of elements)
        element.classList[isHidden ? 'add' : 'remove'](...classes);
    };

    if (startDelay) {
      setTimeout(callback, startDelay);
    } else {
      callback();
    }

    return () => {
      const callback = () => {
        for (const element of elements) {
          element.classList.remove(...classes);
        }
      };

      if (endDelay) {
        setTimeout(callback, endDelay);
      } else {
        callback();
      }
    };

    // The effect should not rerun on `startDelay` or `endDelay` values change
  }, [isHidden, className, hasWindowSize, isSizeThreshold]); // eslint-disable-line react-hooks/exhaustive-deps
}
